export type AdminUserRole = 'super_admin' | 'admin' | 'manager';
export type AdminUserStatusFilter = 'all' | 'active' | 'inactive';

export interface AdminUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: AdminUserRole;
  avatar?: string;
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
  modulePermissions: string[];
  effectivePermissions: string[];
  accessProfileId?: string;
}

export interface AccessProfile {
  id: string;
  name: string;
  baseRole: 'admin' | 'manager';
  modules: string[];
}

export interface PaginatedAdminUsers {
  results: AdminUser[];
  total: number;
  page: string | number;
  limit: string | number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface AdminUserListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: 'active' | 'inactive';
  role?: AdminUserRole;
}

export interface CreateAdminUserPayload {
  firstName: string;
  lastName: string;
  email: string;
  role: AdminUserRole;
  password: string;
  isActive?: boolean;
  modulePermissions?: string[];
  accessProfileId?: string | null;
}

export type UpdateAdminUserPayload = Partial<Omit<CreateAdminUserPayload, 'password'>> & {
  password?: string;
};

export interface AdminModuleDefinition {
  key: string;
  label: string;
  group: string;
  route: string;
}

export interface PermissionsConfig {
  modules: AdminModuleDefinition[];
  rolePermissions: Record<AdminUserRole, string[]>;
  customProfiles: AccessProfile[];
  defaults: Record<string, string[]>;
}

export interface CreateAccessProfilePayload {
  name: string;
  baseRole: 'admin' | 'manager';
  modules: string[];
}

export interface UpdateAccessProfilePayload {
  name?: string;
  baseRole?: 'admin' | 'manager';
  modules?: string[];
}

export interface UpdateRolePermissionsPayload {
  role: 'admin' | 'manager';
  modules: string[];
}

export interface BulkAdminUserIdsPayload {
  ids: string[];
}

export interface BulkAdminUserStatusPayload extends BulkAdminUserIdsPayload {
  isActive: boolean;
}

export interface ResetAdminUserPasswordPayload {
  password: string;
}

export const ADMIN_USER_ROLE_OPTIONS = [
  { label: 'Super Admin', value: 'super_admin' as const },
  { label: 'Admin', value: 'admin' as const },
  { label: 'Manager', value: 'manager' as const },
];

export const ROLE_PERMISSIONS = [
  {
    role: 'super_admin' as const,
    label: 'Super Admin',
    description: 'Full access to all modules, settings, and user management.',
    permissions: [
      'Manage all admin users and roles',
      'Configure website, payment, and SMTP settings',
      'Full catalog, orders, and customer access',
      'View reports and export data',
    ],
  },
  {
    role: 'admin' as const,
    label: 'Admin',
    description: 'Manage day-to-day store operations without system configuration.',
    permissions: [
      'Manage products, categories, and inventory',
      'Process orders and customer accounts',
      'Manage coupons, banners, and content',
      'View reports',
    ],
  },
  {
    role: 'manager' as const,
    label: 'Manager',
    description: 'Limited operational access for fulfillment and catalog updates.',
    permissions: [
      'View and update orders',
      'Manage inventory levels',
      'View products and customers',
      'Approve product reviews',
    ],
  },
];
